'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  Col, Container, Row, Input, Label, Form, FormGroup, FormFeedback,
} from 'reactstrap';
import Image from 'next/image';

// Services
import FeatherIcon from 'feather-icons-react';
import { sendMetrics } from '../../services/ga.service';
import { loginAction } from '../../lib/features/auth/authSlice';
import { useAppDispatch } from '../../lib/hooks';
import { getGeoIp, resendConfirmation } from '../../services/users.service';
import { getOrCreateSessionId } from '../../services/tracking.service';
import { detectTimezoneSync } from '../../services/timezone.service';
import {
  safeReturnPath, peekReturnPath, clearReturnPath, authFlowOf,
} from '../../utils/returnPath';

// Components
import ErrorMessage from '../components/Shared/ErrorMessage';

// Import Icons
import shape from '../../assets/images/shape01.png';
import logoIcon from '../../assets/images/logo-icon.png';

// Stable machine codes for GA4 — the backend's wording is free text and unsafe as a
// dimension. Separates "wrong password" from "account not confirmed" from "server
// down", which is the whole question behind the 28% login conversion.
const loginErrorReason = (message = '') => {
  const text = String(message).toLowerCase();
  if (text.includes('confirm') || text.includes('verif')) return 'account_not_confirmed';
  if (text.includes('password') || text.includes('credential')) return 'wrong_credentials';
  if (text.includes('not found') || text.includes('no such') || text.includes('exist')) return 'user_not_found';
  if (text.includes('block') || text.includes('ban') || text.includes('lock')) return 'account_blocked';
  if (text.includes('fetch') || text.includes('network') || text.includes('abort')) return 'network_error';
  return 'unknown';
};

// `from` arrives decoded once by URLSearchParams; older links encoded it twice.
const decodeFrom = (from) => {
  if (!from) return null;
  try {
    return safeReturnPath(decodeURIComponent(from));
  } catch {
    return safeReturnPath(from);
  }
};

const Login = ({ searchParams }) => {
  const [message, setMessage] = useState(null);
  // The address the last failure was about. Kept separately because the form is
  // cleared on error, and the offer to resend has to know where to send.
  const [unconfirmedEmail, setUnconfirmedEmail] = useState(null);
  const [resendState, setResendState] = useState('idle'); // idle | sending | sent | failed
  const [eInvalid, setEinvalid] = useState(false);
  const [ip, setIp] = useState('');
  const { from, expired, revoked } = searchParams;
  const [login, setLogin] = useState({
    password: '',
    email: '',
    remember: true,
  });
  const [subError, setSubError] = useState('');

  const route = useRouter();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const asyncget = async () => {
      const ip = await getGeoIp();
      setIp(ip.IPv4);
    };
    asyncget();
  }, []);

  // Validation functions
  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

  // Save credentials in state
  const onHandleChange = (e) => {
    const { id, value } = e.target;
    if (id === 'email' && isValidEmail(value)) {
      setEinvalid(false);
    }
    if (id === 'remember') {
      setLogin({
        ...login,
        remember: !login.remember,
      });
      return;
    }
    setLogin({
      ...login,
      [id]: value,
    });
  };

  const emailOnBlur = () => (!isValidEmail(login.email) && setEinvalid(true));
  const onClear = () => (setSubError(''));

  const onSubmit = async (e) => {
    const { password, email } = login;
    e.preventDefault();
    if (!isValidEmail(email)) {
      sendMetrics('login_error', { error_code: 0, error_reason: 'client_validation', field: 'email' });
      setSubError('Email is not valid');
      return;
    }
    if (password.length < 6) {
      sendMetrics('login_error', {
        error_code: 0,
        error_reason: 'client_validation',
        field: 'password',
      });
      setSubError('Password length should be at least 6 symbols');
      return;
    }
    try {
      const sessionId = getOrCreateSessionId(); // Ensure a session id reaches the backend for linking
      const timezone = detectTimezoneSync(); // Get user timezone (sync version for faster login)
      const res = await dispatch(loginAction({
        email, password, ip, sessionId, timezone,
      }));
      if (res.error) {
        // 391 users reached this page and 110 logged in. Nothing said whether the
        // other 281 were wrong passwords, unconfirmed accounts or a failing request.
        sendMetrics('login_error', {
          error_code: res.error.code ?? 0,
          error_reason: loginErrorReason(res.error.message),
          field: 'form',
        });
        setMessage(res.error.message);
        // "Not confirmed" is the one login failure the user cannot act on from
        // here — there is no link to click and no page to visit. Offer the only
        // action that helps instead of leaving them at a wall.
        setUnconfirmedEmail(
          loginErrorReason(res.error.message) === 'account_not_confirmed' ? email : null,
        );
        setResendState('idle');
        setLogin({
          ...login,
          email: '',
          password: '',
        });
        return;
      }
      // Without ?from=, fall back to a path remembered at sign-up (the email
      // confirmation link opens in a new tab and loses the query string).
      const remembered = peekReturnPath();
      const target = decodeFrom(from) || remembered || '/';

      sendMetrics('login', {
        method: 'onSite',
        // 'oauth' when this login continues an MCP connector consent request.
        flow: authFlowOf(target),
      });

      // Give redux-persist time to save the state before redirecting
      await new Promise((resolve) => { setTimeout(resolve, 300); });

      // Temporarily disable auth-survey redirect.
      if (target === remembered) clearReturnPath();
      route.push(target);
    } catch (err) {
      console.log(err);
      sendMetrics('login_error', {
        error_code: err?.statusCode ?? 0,
        error_reason: 'thrown_exception',
        field: 'form',
      });
      setLogin({
        ...login,
        email: '',
        password: '',
      });
    }
  };

  const resend = async () => {
    if (resendState === 'sending' || resendState === 'sent') return;
    setResendState('sending');
    try {
      await resendConfirmation(unconfirmedEmail);
      setResendState('sent');
    } catch {
      setResendState('failed');
    }
  };

  const error = message ? <ErrorMessage text={message} /> : null;
  const submitError = subError ? <ErrorMessage text={subError} onDismiss={onClear} /> : null;
  let expiredTokenMessage = null;
  if (revoked) {
    expiredTokenMessage = (
      <ErrorMessage
        title="Signed out."
        text="Your password was changed or you logged out of all devices. Please log in again."
        color="warning"
      />
    );
  } else if (expired) {
    expiredTokenMessage = (
      <ErrorMessage
        text="Your session has expired. Please log in again to continue."
        color="warning"
      />
    );
  }

  return (
    <>
      <div className="back-to-home">
        <Link href="/" className="back-button btn btn-icon btn-primary">
          <i>
            <FeatherIcon icon="arrow-left" className="icons" />
          </i>
        </Link>
      </div>
      <section
        className="bg-home d-flex align-items-center position-relative"
        style={{ backgroundImage: `url(${shape.src}) center` }}
      >
        <Container>
          <Row>
            <Col xs={12}>
              <div className="form-signin p-4 bg-white rounded shadow">
                <Form onSubmit={onSubmit}>
                  <Link href="/">
                    <Image
                      src={logoIcon}
                      width={65}
                      height={65}
                      className="avatar avatar-small mb-4 d-block mx-auto"
                      alt="logo"
                    />
                  </Link>
                  <h5 className="mb-3 text-center">Please sign in</h5>

                  <FormGroup>
                    <div className="form-floating mb-2">
                      <Input
                        type="email"
                        className="form-control"
                        id="email"
                        placeholder="name@example.com"
                        invalid={eInvalid}
                        value={login.email}
                        onChange={onHandleChange}
                        onBlur={emailOnBlur}
                      />
                      <Label for="floatingEmail">Email Address</Label>
                      <FormFeedback>
                        Oh no! Email is not valid.
                      </FormFeedback>
                    </div>
                  </FormGroup>

                  <FormGroup>
                    <div className="form-floating mb-2">
                      <Input
                        type="password"
                        className="form-control"
                        id="password"
                        placeholder="Password"
                        value={login.password}
                        onChange={onHandleChange}
                      />
                      <Label for="floatingEmail">Password</Label>
                    </div>
                  </FormGroup>

                  <div className="d-flex justify-content-between">
                    <div className="mb-3">
                      {/* <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={login.remember}
                          onChange={onHandleChange}
                          id="remember"
                        />
                        <label className="form-check-label" htmlFor="remember">Remember me</label>
                      </div> */}
                    </div>
                    <p className="forgot-pass mt-3"><Link href="/auth-forgot-password" className="text-dark small fw-bold">Forgot password ?</Link></p>
                  </div>
                  <button className="btn btn-primary w-100" type="submit">Sign in</button>

                  {expiredTokenMessage}
                  {error}
                  {submitError}

                  {unconfirmedEmail && (
                    <div className="alert alert-warning mt-3 mb-0" role="alert">
                      {resendState === 'sent' ? (
                        <small>
                          If that address needs confirming, a new link is on its way to
                          {' '}
                          <strong>{unconfirmedEmail}</strong>
                          . Check the spam folder too.
                        </small>
                      ) : (
                        <>
                          <small className="d-block mb-2">
                            This account has not confirmed its email address yet. We can send the
                            confirmation link again.
                          </small>
                          <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={resend}
                            disabled={resendState === 'sending'}
                          >
                            {resendState === 'sending' ? 'Sending…' : 'Resend confirmation email'}
                          </button>
                          {resendState === 'failed' && (
                            <small className="d-block mt-2">
                              That did not go through. Please try again in a few minutes.
                            </small>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  <div className="col-12 text-center mt-3">
                    <p className="mb-0 mt-3">
                      <small className="text-dark me-2">Don&apos;t have an account ?</small>
                      {' '}
                      <Link href={from ? `/auth-signup?from=${encodeURIComponent(from)}` : '/auth-signup'} className="text-dark fw-bold">Sign Up</Link>
                    </p>
                  </div>

                  <p className="mb-0 text-muted mt-3 text-center" suppressHydrationWarning>
                    ©
                    {new Date().getFullYear()}
                    {' '}
                    svghmi.pro
                  </p>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Login;
